import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  mxApiClient,
} from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  ActiveReadingStatus,
  Formats,
  MeterReaderList,
  ReadingFormatList,
  ReadingStatusList,
  ReasonCodeList,
  UtilityService,
} from "./types";
import { PAGE_SIZE } from "@shared/utils/constants";

export const getMeterReadingFormat = async (
  remoteUtilityId: string,
  search: string
): Promise<ReadingFormatList[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading-format",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      if (search) {
        query.push("search_data", search);
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ReadingFormatList[]>(response.data.result);
};

export const getUserUtilityServices = async (
  utilityId: string
): Promise<UtilityService[]> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "auth",
    "utility",
    ["services"],
    (query) => {
      query.push("utility_id", utilityId);
    }
  );

  const response = await authApiClient.get(url);
  return transformJsonToObject<UtilityService[]>(response.data.result);
};

  export const  deleteManufacturere= async (id: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      `meter/manufacturer/${id}`,
     
    );
    const response = await mxApiClient.delete(url);
    return response.data;
  };


export const postMeterReadingFormat = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "meter-reading-format");
  const response = await mxApiClient.post(url, payload);
  return response?.data;
};

export const getMeterReadingFormatDetail = async (
  id: string,
  remoteUtilityId: string
): Promise<Formats> => {
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "mx",
    "meter-reading-format/detail",
    [id],
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );
  const response = await mxApiClient.get(url);
  return transformJsonToObject<Formats>(response.data.result);
};

export const patchMeterReadingFormat = async (id: number, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-reading-format",
    [id]
  );

  const response = await mxApiClient.patch(url, payload);
  return response.data;
};

export const getMeterReaderList = async ({
  page,
  remoteUtilityId,
  search,
}: {
  page: number;
  remoteUtilityId: string;
  search?: string;
}): Promise<MeterReaderList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reader",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("page", String(page));
      query.push("limit", 10);
      if (search) {
        query.push("search_data", search);
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<MeterReaderList>(response.data);
};

export const updateMeterReader = async (id: number, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "meter-reader", [
    id,
  ]);
  const response = await mxApiClient.put(url, payload);
  return response.data;
};

export const getMeterReadingStatusList = async (
  remoteUtilityId: string,
  page: number,
  limit: any,
  search?: string
): Promise<ReadingStatusList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading-status",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_pagination_required", true);
      query.push("page", page);
      query.push("limit", limit);
      if (search) {
        query.push("search_data", search);
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ReadingStatusList>(response.data);
};

export const updateMeterReadingStatus = async (id: string, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables(
    "mx",
    "meter-reading-status",
    [id]
  );

  const response = await mxApiClient.put(url, payload);
  return response?.data;
};

export const postMeterReadingStatus = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "meter-reading-status");
  const response = await mxApiClient.post(url, payload);
  return response.data;
};

export const getMeterReasonCodesList = async (
  remoteUtilityId: string,
  page,
  limit: number,
  search?: string
): Promise<ReasonCodeList> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "reason-code",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_pagination_required", true);
      query.push("page", page);
      query.push("limit", limit);
      if (search) {
        query.push("search_data", search);
      }
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ReasonCodeList>(response.data);
};

export const updateMeterReasonCodes = async (id: string, payload: any) => {
  const url = ApiEndpoints.createUrlWithPathVariables("mx", "reason-code", [
    id,
  ]);

  const response = await mxApiClient.put(url, payload);
  return response?.data;
};

export const postMeterReasonCodes = async (payload: any) => {
  const url = ApiEndpoints.createUrl("mx", "reason-code");
  const response = await mxApiClient.post(url, payload);
  return response.data;
};

export const getActiveMeterReadingStatuses = async (
  remoteUtilityId: string
): Promise<ActiveReadingStatus[]> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "meter-reading-status",
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
      query.push("is_active", "True"); // Hardcoded
    }
  );

  const response = await mxApiClient.get(url);
  return transformJsonToObject<ActiveReadingStatus[]>(response.data.result);
};

export const createMeterReader = async (formData: FormData): Promise<any> => {
  const url = ApiEndpoints.createUrl("mx", "meter-reader");
  const response = await mxApiClient.postForm(url, formData);
  return response.data;
};
